/**
 * Type for teacher-feedback user role documents stored in Firestore at:
 * teacherFeedback/auth/users/{uid}/roles/{courseId}
 */

import {z} from 'zod';

// ============================================================================
// Document Schema
// ============================================================================

/**
 * Schema for a user's role within a specific course in the teacher-feedback app.
 * Written by admins or setup scripts; read by the client to gate UI access.
 */
export const TeacherFeedbackUserRoleSchema = z.object({
  /** The user's role for the given course. */
  role: z.enum(['admin', 'teacher']),
});

/** A user's role for a specific course in the teacher-feedback app. */
export type TeacherFeedbackUserRole = z.infer<
  typeof TeacherFeedbackUserRoleSchema
>;

/**
 * The role string values available in the teacher-feedback app.
 * Derived from the schema so it stays in sync with validation.
 */
export type TeacherFeedbackRole = TeacherFeedbackUserRole['role'];

// ============================================================================
// Paths
// ============================================================================

/** Root Firestore collection for all teacher-feedback app data. */
export const TEACHER_FEEDBACK_COLLECTION = 'teacherFeedback';

/**
 * Returns the path to a user's roles subcollection.
 * Path: teacherFeedback/auth/users/{uid}/roles
 */
export function getTeacherFeedbackRolesCollectionPath({
  uid,
}: {
  uid: string;
}): string {
  return `${TEACHER_FEEDBACK_COLLECTION}/auth/users/${uid}/roles`;
}

/**
 * Returns the path to a user's role document for a specific course.
 * Path: teacherFeedback/auth/users/{uid}/roles/{courseId}
 */
export function getTeacherFeedbackRoleDocumentPath({
  uid,
  courseId,
}: {
  uid: string;
  courseId: string;
}): string {
  return `${TEACHER_FEEDBACK_COLLECTION}/auth/users/${uid}/roles/${courseId}`;
}
